import { Link } from 'react-router-dom';
import { ServiceGraceBanner } from '@/components/ServiceGraceBanner';
import { HIRE_LIST_HEADER_FLOW, HIRE_PAGE_CONTAINER, HIRE_SECTION_SPACING, HIRE_STAT_GRID, HIRE_STAT_PAD, HIRE_PAGE_HEADER_PAD } from '@/lib/hireResponsive';

import { AssignRecruiterSelect } from '@/components/AssignRecruiterSelect';
import { AppLayout } from '@/components/AppLayout';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { StageBadge } from '@/components/StageBadge';
import { ScoreBadge } from '@/components/ScoreBadge';
import { useJobs, type Job } from '@/hooks/useJobs';
import { useInterviewConfig } from '@/hooks/useInterviewConfig';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Video, MapPin, Building2, CheckCircle2, FileEdit, Users, Search, Clock, BarChart3, Briefcase, ArrowRight, LayoutList, LayoutGrid, ChevronDown, ChevronRight } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useTranslation } from 'react-i18next';
import { memo, useState, useMemo } from 'react';
import { formatLocalizedDate, formatRelativeTime } from '@/lib/localizedDate';
import { formatJobId } from '@/lib/jobId';
import { formatInterviewId } from '@/lib/interviewId';
import { hasPublishedInterviewQuestions } from '@/lib/forkhire/interview/interviewReadiness';
import { interviewTerminalPresentationKind } from '@/lib/forkhire/interview/interviewIncompletePresentation';

const InterviewSessionCount = memo(function InterviewSessionCount({ jobId }: { jobId: string }) {
  const { t } = useTranslation();
  const { data: count, isLoading } = useQuery({
    queryKey: ['interview-session-count', jobId],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('interview_sessions')
        .select('id, applications!inner(job_id)', { count: 'exact', head: true })
        .eq('applications.job_id', jobId);
      if (error) return 0;
      return count || 0;
    },
  });
  if (isLoading || !count) return null;
  return (
    <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
      <Users className="w-3.5 h-3.5" />
      {count} {t('hire.interviews.candidates')}
    </span>
  );
});

const InterviewStatus = memo(function InterviewStatus({ jobId }: { jobId: string }) {
  const { t } = useTranslation();
  const { data: config, isLoading } = useInterviewConfig(jobId);
  if (isLoading) return <Skeleton className="h-6 w-20 rounded-full" />;
  if (!config) return <Badge variant="outline" className="text-xs rounded-full px-3 py-1">{t('hire.interviews.notCreated')}</Badge>;
  if (hasPublishedInterviewQuestions(config)) {
    const count = Array.isArray(config.questions_json) ? config.questions_json.length : 0;
    return (
      <Badge className="text-xs bg-success/10 text-success border-success/20 rounded-full px-3 py-1">
        <CheckCircle2 className="w-3.5 h-3.5 me-1" />
        {t('hire.interviews.publishedWithQuestions', { count })}
      </Badge>
    );
  }
  return (
    <Badge variant="secondary" className="text-xs rounded-full px-3 py-1">
      <FileEdit className="w-3.5 h-3.5 me-1" />
      {t('hire.interviews.draft')}
    </Badge>
  );
});

// Inline collapsible candidate sessions for a job
const JobCandidateSessions = memo(function JobCandidateSessions({ jobId }: { jobId: string }) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? i18n.language;
  const { data: sessions, isLoading } = useQuery({
    queryKey: ['interview-candidates-inline', jobId],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('interview_sessions')
        .select(`
          id, status, token, expires_at, completed_at,
          overall_score, failure_reason, terminal_reason_code, created_at, session_number,
          applications!inner (
            id, candidate_id, job_id, assigned_to,
            candidates!inner ( id, name, email )
          )
        `)
        .eq('applications.job_id', jobId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data || []).map((s) => ({
        id: s.id,
        status: s.status,
        token: s.token,
        session_number: s.session_number,
        overall_score: s.overall_score,
        failure_reason: s.failure_reason,
        terminal_reason_code: s.terminal_reason_code,
        completed_at: s.completed_at,
        expires_at: s.expires_at,
        created_at: s.created_at,
        application_id: s.applications?.id || '',
        candidate_id: s.applications?.candidate_id || s.applications?.candidates?.id || '',
        assigned_to: s.applications?.assigned_to ?? null,
        candidate_name: s.applications?.candidates?.name || '',
        candidate_email: s.applications?.candidates?.email || '',
      }));
    },
  });

  if (isLoading) return <div className="px-5 py-3"><Skeleton className="h-8 w-full" /></div>;
  if (!sessions || sessions.length === 0) {
    return (
      <div className="px-5 py-4 text-xs text-muted-foreground italic">
        {t('hire.interviews.noCandidateSessions')}
      </div>
    );
  }

  const statusColors: Record<string, string> = {
    invited: 'bg-amber-500/10 text-amber-600',
    in_progress: 'bg-blue-500/10 text-blue-600',
    completed: 'bg-success/10 text-success',
    scored: 'bg-primary/10 text-primary',
    expired: 'bg-destructive/10 text-destructive',
    incomplete: 'bg-amber-500/10 text-amber-600',
  };

  const sessionStatusLabel = (status: string) => {
    const key = status.replace(/[^a-zA-Z0-9_]/g, '');
    return t(`hire.interviews.sessionStatuses.${key}`, { defaultValue: status.replace('_', ' ') });
  };

  return (
    <div className="bg-muted/30 border-t border-border">
      <div className="divide-y divide-border/50">
        {sessions.map((s) => {
          const isExpired = s.status === 'invited' && s.expires_at && new Date(s.expires_at) < new Date();
          const effectiveStatus = isExpired ? 'expired' : s.status;
          const terminalKind = interviewTerminalPresentationKind({
            status: s.status,
            terminalReasonCode: s.terminal_reason_code,
            failureReason: s.failure_reason,
          });
          const initials = s.candidate_name
            .split(' ')
            .map((n: string) => n[0])
            .join('')
            .toUpperCase();
          return (
            <Link
              key={s.id}
              to={`/candidates/${s.candidate_id}?tab=interview&app=${s.application_id}&session=${s.id}`}
              className="flex flex-col sm:flex-row sm:items-center gap-3 px-5 py-2.5 ps-14 hover:bg-muted/30 transition-colors"
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-primary/10 border border-border flex items-center justify-center flex-shrink-0">
                  <Video className="w-4 h-4 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-muted text-muted-foreground border border-border whitespace-nowrap">
                      {formatInterviewId(s.session_number)}
                    </span>
                    <p className="font-medium text-sm text-foreground truncate">{s.candidate_name}</p>
                    <Badge variant="outline" className={`text-[10px] px-2 py-0.5 capitalize ${statusColors[effectiveStatus] || ''}`}>
                      {sessionStatusLabel(effectiveStatus)}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{s.candidate_email}</p>
                </div>
                <div className="pt-0.5 shrink-0" onClick={(e) => e.preventDefault()}>
                  <AssignRecruiterSelect applicationId={s.application_id} assignedTo={s.assigned_to} compact />
                </div>
              </div>
              <div className="flex items-center gap-3 ms-[52px] sm:ms-0 flex-shrink-0">
                {s.overall_score != null ? (
                  <ScoreBadge score={s.overall_score} size="sm" type="interview" />
                ) : terminalKind ? (
                  <div className="text-end">
                    <p className="text-sm font-bold text-destructive" dir="ltr">0%</p>
                    <p className="text-[10px] text-muted-foreground">{t('hire.candidateDetail.interviewOutcomes.notAiScored')}</p>
                  </div>
                ) : null}
                <span className="text-xs text-muted-foreground whitespace-nowrap">{formatRelativeTime(s.created_at, language)}</span>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
});

// Hook to get interview KPI stats
function useInterviewKpis(jobs: Job[] | undefined) {
  return useQuery({
    queryKey: ['interview-kpis', jobs?.map(j => j.id)],
    enabled: !!jobs && jobs.length > 0,
    queryFn: async () => {
      const activeJobIds = (jobs || []).filter(j => j.status === 'active').map(j => j.id);
      if (activeJobIds.length === 0) return { total: 0, completionRate: 0, avgDuration: 0, passedCandidates: 0 };

      // Total interviews (all sessions)
      const { count: total } = await supabase
        .from('interview_sessions')
        .select('id, applications!inner(job_id)', { count: 'exact', head: true })
        .in('applications.job_id', activeJobIds);

      // Completed sessions
      const { count: completed } = await supabase
        .from('interview_sessions')
        .select('id, applications!inner(job_id)', { count: 'exact', head: true })
        .in('applications.job_id', activeJobIds)
        .in('status', ['completed', 'scored']);

      // Passed candidates — scored >= job threshold
      const { data: jobsData } = await supabase
        .from('jobs')
        .select('id, interview_pass_threshold')
        .in('id', activeJobIds);

      let passedCandidates = 0;
      if (jobsData && jobsData.length > 0) {
        const { data: scoredSessions } = await supabase
          .from('interview_sessions')
          .select('overall_score, applications!inner(job_id)')
          .in('applications.job_id', activeJobIds)
          .in('status', ['completed', 'scored'])
          .not('overall_score', 'is', null);

        const thresholdMap: Record<string, number> = {};
        jobsData.forEach((job) => { thresholdMap[job.id] = job.interview_pass_threshold ?? 70; });

        passedCandidates = (scoredSessions || []).filter((session) => {
          const threshold = thresholdMap[session.applications?.job_id] ?? 70;
          return (session.overall_score ?? 0) >= threshold;
        }).length;
      }

      // Avg duration from responses
      const { data: responses } = await supabase
        .from('interview_responses')
        .select('duration_sec, session_id, interview_sessions!inner(application_id, applications!inner(job_id))')
        .in('interview_sessions.applications.job_id', activeJobIds)
        .not('duration_sec', 'is', null);

      const sessionDurations: Record<string, number> = {};
      (responses || []).forEach(r => {
        sessionDurations[r.session_id] = (sessionDurations[r.session_id] || 0) + (r.duration_sec || 0);
      });
      const sessionCount = Object.keys(sessionDurations).length;
      const avgDurationMin = sessionCount > 0 ? Math.round(Object.values(sessionDurations).reduce((a, b) => a + b, 0) / sessionCount / 60) : 0;

      const completionRate = (total || 0) > 0 ? Math.round(((completed || 0) / (total || 1)) * 100) : 0;

      return {
        total: total || 0,
        completionRate,
        avgDuration: avgDurationMin,
        passedCandidates,
      };
    },
  });
}

export default function Interviews() {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? i18n.language;
  const { data: jobs, isLoading } = useJobs();
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('active');
  const [viewMode, setViewMode] = useState<'bar' | 'card'>('bar');

  const allJobs = jobs || [];
  const activeJobs = allJobs.filter((job) => job.status === 'active');
  const inactiveJobs = allJobs.filter((job) => job.status === 'inactive');
  const closedJobs = allJobs.filter((job) => ['closed', 'expired'].includes(job.status));

  const { data: kpis } = useInterviewKpis(jobs);

  const [expandedJobs, setExpandedJobs] = useState<Set<string>>(new Set());
  const toggleJob = (jobId: string) => {
    setExpandedJobs(prev => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId); else next.add(jobId);
      return next;
    });
  };

  // Fetch all interview config statuses for sorting
  const jobIds = allJobs.map((job) => job.id);
  const { data: configStatuses } = useQuery({
    queryKey: ['interview-config-statuses', jobIds],
    enabled: jobIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('interview_configs')
        .select('job_id, status, questions_json, updated_at')
        .in('job_id', jobIds)
        .order('updated_at', { ascending: false });
      if (error) return {};
      const map: Record<string, string> = {};
      (data || []).forEach((config) => {
        if (!map[config.job_id]) {
          map[config.job_id] = hasPublishedInterviewQuestions(config) ? 'published' : config.status;
        }
      });
      return map;
    },
  });

  const currentList = useMemo(() => {
    let list: Job[];
    switch (tab) {
      case 'inactive': list = inactiveJobs; break;
      case 'closed': list = closedJobs; break;
      default: list = activeJobs;
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((job) => {
        const jobIdStr = job.job_number ? `job-${String(job.job_number).padStart(4, '0')}` : '';
        return job.title.toLowerCase().includes(q) ||
          (job.department || '').toLowerCase().includes(q) ||
          (job.location || '').toLowerCase().includes(q) ||
          jobIdStr.includes(q) ||
          String(job.job_number || '').includes(q);
      });
    }
    // Sort: published interviews first, then draft, then not created
    if (configStatuses) {
      list = [...list].sort((a, b) => {
        const statusA = configStatuses[a.id] || '';
        const statusB = configStatuses[b.id] || '';
        const order = (s: string) => s === 'published' ? 0 : s === 'draft' ? 1 : 2;
        return order(statusA) - order(statusB);
      });
    }
    return list;
  }, [tab, search, activeJobs, inactiveJobs, closedJobs, configStatuses]);

  return (
    <AppLayout>
      <div className={`${HIRE_PAGE_CONTAINER} ${HIRE_SECTION_SPACING}`}>
        {/* Grace Period Banner */}
        <ServiceGraceBanner serviceKey="ai_interviews" />
        {/* Header Card — unified with Jobs/Candidates */}
        <div className={HIRE_LIST_HEADER_FLOW}>
          <Card className="shadow-card border-border border-t-4 border-t-primary overflow-hidden">
            <div className={`${HIRE_PAGE_HEADER_PAD} flex flex-wrap items-center justify-between gap-4`}>
              <div>
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold font-display text-foreground">{t('hire.interviews.title')}</h1>
                <p className="text-muted-foreground mt-1.5 text-sm">{t('hire.interviews.subtitle')}</p>
              </div>
            </div>

            {/* KPI Stats */}
            <div className={`${HIRE_STAT_GRID} px-5 sm:px-6 lg:px-8 3xl:px-10 pb-5 sm:pb-6 lg:pb-8 3xl:pb-10`}>
              {[
                { icon: <Video className="w-5 h-5 text-primary" />, value: kpis?.total ?? 0, label: t('hire.interviews.activeSessions'), bg: 'bg-primary/10' },
                { icon: <CheckCircle2 className="w-5 h-5 text-success" />, value: `${kpis?.completionRate ?? 0}%`, label: t('hire.interviews.completionRate'), bg: 'bg-success/10' },
                { icon: <Users className="w-5 h-5 text-blue-500" />, value: kpis?.passedCandidates ?? 0, label: t('hire.interviews.passedCandidates'), bg: 'bg-blue-500/10' },
                { icon: <Clock className="w-5 h-5 text-orange-500" />, value: `${kpis?.avgDuration ?? 0}m`, label: t('hire.interviews.avgDuration'), bg: 'bg-orange-500/10' },
              ].map((stat) => (
                <div key={stat.label} className={`bg-muted/40 border border-border rounded-2xl ${HIRE_STAT_PAD}`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-11 h-11 rounded-xl ${stat.bg} flex items-center justify-center flex-shrink-0`}>
                      {stat.icon}
                    </div>
                    <div>
                      <p className="text-2xl lg:text-3xl font-bold font-display text-foreground tabular-nums">
                        {isLoading ? <Skeleton className="h-8 w-12" /> : stat.value}
                      </p>
                      <p className="text-xs sm:text-sm text-muted-foreground font-medium mt-1">{stat.label}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Search + Tabs row — bottom, matching Candidates */}
            <div className="flex flex-col gap-3 border-t border-border px-5 py-4 sm:px-6 lg:flex-row lg:items-center lg:px-8 lg:py-3">
              <div className="relative w-full min-w-0 lg:flex-1 lg:min-w-[200px]">
                <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder={t('hire.interviews.searchPlaceholder')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="ps-9 h-10 rounded-xl"
                />
              </div>
              <div className="flex w-full flex-wrap items-center gap-1 rounded-xl border border-border bg-muted/50 p-1 sm:w-auto lg:flex-shrink-0">
                {[
                   { key: 'active', label: t('stages.active'), count: activeJobs.length },
                   { key: 'inactive', label: t('stages.inactive'), count: inactiveJobs.length },
                   { key: 'closed', label: t('stages.closed'), count: closedJobs.length },
                ].map((tabItem) => (
                  <button
                    key={tabItem.key}
                    onClick={() => setTab(tabItem.key)}
                    className={`min-h-9 flex-1 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors whitespace-normal sm:flex-none ${
                      tab === tabItem.key
                        ? 'bg-card text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {tabItem.label}
                    {tabItem.key === 'active' && (
                      <span className="ms-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold">
                        {tabItem.count}
                      </span>
                    )}
                  </button>
                ))}
              </div>
              {/* View toggle */}
              <div className="flex w-fit items-center gap-1 rounded-xl border border-border bg-muted/50 p-1 lg:flex-shrink-0">
                <button
                  onClick={() => setViewMode('bar')}
                  className={`p-1.5 rounded-lg transition-colors ${viewMode === 'bar' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                  title={t('hire.interviews.listView')}
                >
                  <LayoutList className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('card')}
                  className={`p-1.5 rounded-lg transition-colors ${viewMode === 'card' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                  title={t('hire.interviews.cardView')}
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
              </div>
            </div>
          </Card>
        </div>

        {/* Job List */}
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-24 w-full rounded-xl" />
            ))}
          </div>
        ) : currentList.length === 0 ? (
          <Card className="p-10 sm:p-12 text-center shadow-card border-border rounded-xl">
            <Video className="w-14 h-14 text-muted-foreground/30 mx-auto mb-4" />
            <h2 className="font-display font-semibold text-foreground text-lg mb-2">{t('hire.interviews.noActiveJobs')}</h2>
            <p className="text-sm text-muted-foreground readable mx-auto">
              {t('hire.interviews.noActiveJobsDesc')}
            </p>
          </Card>
        ) : viewMode === 'bar' ? (
          /* ── Bar / List View ── */
          <Card className="shadow-card border-border border-s-4 border-s-primary rounded-xl overflow-hidden divide-y divide-border">
            {currentList.map((job) => (
              <Collapsible key={job.id} open={expandedJobs.has(job.id)} onOpenChange={() => toggleJob(job.id)}>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 px-5 py-4 hover:bg-muted/40 transition-colors group">
                  <CollapsibleTrigger asChild>
                    <button className="flex items-center justify-center w-6 h-6 rounded hover:bg-muted transition-colors flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                      {expandedJobs.has(job.id) ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                    </button>
                  </CollapsibleTrigger>
                  <Link to={`/interviews/${job.id}`} className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Briefcase className="w-4 h-4 text-primary" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                       <p className="font-semibold text-sm text-foreground">{job.title}</p>
                        {job.job_number && (
                          <span className="inline-flex items-center text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-muted text-muted-foreground border border-border whitespace-nowrap">{formatJobId(job.job_number)}</span>
                        )}
                        <StageBadge stage={job.status} />
                      </div>
                      <div className="flex items-center gap-x-4 gap-y-1 text-xs text-muted-foreground mt-1.5 flex-wrap">
                        {job.location && (
                          <span className="inline-flex items-center gap-1">
                            <MapPin className="w-3 h-3 flex-shrink-0" /> {job.location}
                          </span>
                        )}
                        {job.department && (
                          <span className="inline-flex items-center gap-1 text-muted-foreground">{job.department}</span>
                        )}
                        {job.employment_type && (
                          <span className="capitalize">{job.employment_type}</span>
                        )}
                        <span className="inline-flex items-center gap-1">
                          <Clock className="w-3 h-3 flex-shrink-0" /> {formatLocalizedDate(job.created_at, 'PP', language)}
                        </span>
                        <InterviewSessionCount jobId={job.id} />
                      </div>
                    </div>
                  </Link>
                  <div className="flex items-center gap-2 flex-shrink-0 sm:ms-auto">
                    <InterviewStatus jobId={job.id} />
                    <Link to={`/interviews/${job.id}`}>
                      <ArrowRight className="w-4 h-4 text-muted-foreground/40 hover:text-muted-foreground transition-colors ms-1 rtl-flip" />
                    </Link>
                  </div>
                </div>
                <CollapsibleContent forceMount className={expandedJobs.has(job.id) ? '' : 'hidden'}>
                  <JobCandidateSessions jobId={job.id} />
                </CollapsibleContent>
              </Collapsible>
            ))}
          </Card>
        ) : (
          /* ── Card / Grid View ── */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {currentList.map((job) => (
              <Link key={job.id} to={`/interviews/${job.id}`} className="block group">
                <Card className="shadow-card border-border border-t-4 border-t-primary rounded-xl p-5 hover:shadow-lg transition-all h-full flex flex-col gap-3">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Briefcase className="w-5 h-5 text-primary" />
                    </div>
                    <div className="min-w-0 flex-1">
                       <p className="font-semibold text-sm text-foreground truncate">{job.title}</p>
                        {job.job_number && (
                          <span className="inline-flex items-center text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-muted text-muted-foreground border border-border whitespace-nowrap mt-0.5">{formatJobId(job.job_number)}</span>
                        )}
                      <div className="mt-1">
                        <StageBadge stage={job.status} />
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground/0 group-hover:text-muted-foreground transition-colors flex-shrink-0 mt-1 rtl-flip" />
                  </div>
                  <div className="flex flex-col gap-1.5 text-xs text-muted-foreground mt-auto">
                    {job.location && (
                      <span className="inline-flex items-center gap-1.5">
                        <MapPin className="w-3 h-3 flex-shrink-0" /> {job.location}
                      </span>
                    )}
                    {job.department && (
                      <span className="inline-flex items-center gap-1.5">
                        <Building2 className="w-3 h-3 flex-shrink-0" /> {job.department}
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1.5">
                      <Clock className="w-3 h-3 flex-shrink-0" /> {formatLocalizedDate(job.created_at, 'PP', language)}
                    </span>
                    <InterviewSessionCount jobId={job.id} />
                  </div>
                  <div className="pt-2 border-t border-border">
                    <InterviewStatus jobId={job.id} />
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
